import { FC, useEffect, useMemo } from 'react';

import { useRecoilValue, useSetRecoilState } from 'recoil';

import CourtesyTopBanner from '@service/bugo/components/CourtesyTopBanner';
import {
  generateFallbackCourtesyAssetList,
  generateFallbackCourtesyContentList,
} from '@service/bugo/utils/courtesyContentGenerator';
import { BugoData } from '@shared/api/bugo/bugo.interface';
import {
  courtesyAssetListByMemberSelector,
  courtesyContentListByMemberSelector,
} from '@shared/api/courtesyAsset/courtesyAsset.selector';
import { trackGiftEvent } from '@shared/api/giftEventLog/giftEventLog.controller';
import { Button } from '@shared/components/Button';
import Modal from '@shared/components/Modal';
import { WebCourtesyShareModal } from '@shared/containers/WebCourtesyShareModal/WebCourtesyShareModal';
import { simpleOpenAtomFamily } from '@shared/state/atom';
import { Link } from 'react-router-dom';
import giftPromCourtesyFooter from 'src/assets/gift/gift-prom-courtesy-footer.png';
import giftPromModalButton from 'src/assets/gift/gift-prom-modal-button.png';

import { MemberCourtesyAssetSelector } from './MemberCourtesyAssetSelecter';
import { MemberCourtesyContentSelector } from './MemberCourtesyContentSelector';
import { MemberCourtesyPreviewModal } from './MemberCourtesyPreviewModal';
import { courtesyContentStringAtom } from './courtesy.atom';

interface Props {
  bugo: BugoData;
  // 기간이 지난 부고: 고인명·상주명·장례정보를 일절 표시하지 않는다.
  anonymous?: boolean;
}

//* 상주용 답례글 생성기
export const MemberCourtesySender: FC<Props> = function MemberCourtesySender({
  bugo,
  anonymous = false,
}: Props) {
  //* 필요한 state
  const courtesyContentString = useRecoilValue(courtesyContentStringAtom);
  const courtesyAssetListState = useRecoilValue(courtesyAssetListByMemberSelector);
  const courtesyContentListState = useRecoilValue(courtesyContentListByMemberSelector);

  const giftEnabled = bugo?.bugoBrand?.featureFlags?.giftEnabled ?? false;
  const giftPilot = bugo?.user?.bugoAgencyWorkerDetail?.giftPilot ?? false;

  const giftUse = giftEnabled || giftPilot;

  useEffect(() => {
    if (giftUse) trackGiftEvent('courtesy_view', bugo._id);
  }, [giftUse, bugo._id]);

  const memberCourtesyAssetSelector = useMemo(() => {
    if (courtesyAssetListState.status === 'success') {
      const list =
        courtesyAssetListState.data && courtesyAssetListState.data.length > 0
          ? courtesyAssetListState.data
          : generateFallbackCourtesyAssetList();
      return <MemberCourtesyAssetSelector bugo={bugo} courtesyAssetList={list} />;
    }
  }, [bugo, courtesyAssetListState.data, courtesyAssetListState.status]);

  const memberCourtesyCotentSelector = useMemo(() => {
    if (courtesyContentListState.status === 'success') {
      const list =
        courtesyContentListState.data && courtesyContentListState.data.length > 0
          ? courtesyContentListState.data
          : generateFallbackCourtesyContentList();
      return (
        <MemberCourtesyContentSelector
          courtesyContentList={list}
          bugo={bugo}
          anonymous={anonymous}
        />
      );
    }
  }, [anonymous, bugo, courtesyContentListState.data, courtesyContentListState.status]);

  const previewOpenId = `courtesy-preview-${bugo._id}`;
  const setPreviewOpen = useSetRecoilState(simpleOpenAtomFamily(previewOpenId));
  const shareOpenId = `courtesyShare-${bugo._id}`;
  const setShareOpen = useSetRecoilState(simpleOpenAtomFamily(shareOpenId));
  const shareSelectOpenId = `courtesyShareSelect-${bugo._id}`;
  const setShareSelectOpen = useSetRecoilState(simpleOpenAtomFamily(shareSelectOpenId));

  const notOpenGift = true;

  return (
    <div className="space-y-4 pb-16">
      {/* 기간이 지난 부고는 고인 정보(이름·별세일)를 노출하지 않고 구분선만 둔다 */}
      {anonymous ? (
        <div className="border-t border-gray-200" />
      ) : (
        <CourtesyTopBanner fevent={bugo.fevent}></CourtesyTopBanner>
      )}
      {memberCourtesyAssetSelector}
      {memberCourtesyCotentSelector}
      {/* 답례품 구매 유도 배너 */}
      {giftUse && (
        <Link to={`/gift-store?bugoId=${bugo._id}`} className="block px-4">
          <img
            src={giftPromCourtesyFooter}
            alt="답례품과 함께 전달해보세요"
            className="w-full"
          />
        </Link>
      )}
      {/* 답례글 공유 방식 선택 모달 */}
      <Modal openId={shareSelectOpenId} position="center" className="w-full max-w-md">
        <div className="space-y-4">
          <h3 className="text-center text-lg font-bold">공유 방식 선택</h3>
          <div className="grid grid-cols-2 gap-2">
            <Button
              className="flex flex-col items-center justify-center gap-1 py-2.5 text-sm font-semibold text-white theme-bg-main"
              style={{
                paddingBlock: giftUse ? '10px' : '20px',
              }}
              onClick={() => {
                setShareSelectOpen(false);
                history.back();
                setTimeout(() => {
                  // 이전 모달의 scroll lock 잔여 설정 초기화
                  document.body.style.overflow = '';
                  setPreviewOpen(true);
                }, 300);
              }}
            >
              {/* 이미지/사진 아이콘 */}
              <svg
                className="h-5 w-5"
                fill="none"
                stroke="currentColor"
                strokeWidth={1.5}
                viewBox="0 0 24 24"
              >
                <rect x="3" y="3" width="18" height="18" rx="2" />
                <circle cx="8.5" cy="8.5" r="1.5" />
                <path d="M21 15l-5-5L5 21" />
              </svg>
              <span>이미지</span>
            </Button>
            <Button
              className="flex flex-col items-center justify-center gap-1 bg-gray-800 py-2.5 text-sm font-semibold text-white"
              style={{
                paddingBlock: giftUse ? '10px' : '20px',
              }}
              onClick={() => {
                setShareSelectOpen(false);
                history.back();
                setTimeout(() => {
                  // 이전 모달의 scroll lock 잔여 설정 초기화
                  document.body.style.overflow = '';
                  setShareOpen(true);
                }, 300);
              }}
            >
              {/* 글/텍스트 아이콘 */}
              <svg
                className="h-5 w-5"
                fill="none"
                stroke="currentColor"
                strokeWidth={1.5}
                viewBox="0 0 24 24"
              >
                <path d="M4 6h16M4 10h16M4 14h10M4 18h6" strokeLinecap="round" />
              </svg>
              <span>글로만 공유</span>
            </Button>
          </div>

          {/* 답례품 구매 유도 배너 */}
          {giftUse && (
            <Link
              to={`/gift-store?bugoId=${bugo._id}`}
              className="mt-2 block"
              onClick={() => {
                setShareSelectOpen(false);
              }}
            >
              <img
                src={giftPromModalButton}
                alt="답례품과 함께 전달해보세요"
                className="w-full"
              />
            </Link>
          )}
        </div>
      </Modal>
      <MemberCourtesyPreviewModal openId={previewOpenId} />
      <WebCourtesyShareModal
        openId={shareOpenId}
        content={courtesyContentString ?? ''}
        buttonTitle={bugo?.bugoBrand?.bugoAgency?.corpName ?? ''}
        kakaoContent={{
          courtesyContent: courtesyContentString ?? '',
        }}
      />
      <div className="fixed bottom-0 left-0 right-0 grid grid-cols-1 font-bold text-white">
        <Button
          text="답례글 보내기"
          className={`button-rectangle theme-bg-main ${giftUse ? '' : 'col-span-2'}`}
          onClick={() => {
            setShareSelectOpen(true);
          }}
        />
        {giftUse && (
          <Button
            text="답례품 구매"
            className="button-rectangle bg-gray-800"
            to={`/gift-store?bugoId=${bugo._id}`}
          />
        )}
      </div>
    </div>
  );
};
